import { useState } from 'react';
import type { User, Overlap } from '../../types';
import { getAirline } from '../../utils/airlines';
import { getCityName } from '../../utils/airports';
import { formatDateShort, toDateStr } from '../../utils/dates';
import { useApp } from '../../context/AppContext';
import { MapPin, Bell, BellOff, UserMinus, MessageCircle, Crown, ChevronDown, ChevronUp } from 'lucide-react';
import { haptic, hapticMedium } from '../../utils/haptics';
import { FriendProfileSheet } from '../profile/FriendProfileSheet';
import { ChatView } from '../chat/ChatView';
import { PremiumUpsellSheet } from '../premium/PremiumUpsellSheet';
import { FREE_OVERLAP_HORIZON_DAYS } from '../../utils/premiumGating';
import { AvatarImg } from '../shared/Avatar';

interface FriendCardProps {
  friend: User;
  overlaps: Overlap[];
}

export function FriendCard({ friend, overlaps }: FriendCardProps) {
  const { mutedFriendIds, toggleFriendMute, removeFriend, isPremium } = useApp();
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [chatTarget, setChatTarget] = useState<{ partnerId: string; prefill?: string } | null>(null);
  const [showUpsell, setShowUpsell] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const airline = getAirline(friend.airlineCode);
  const isMuted = mutedFriendIds.has(friend.id);

  // Find ALL overlaps with this friend
  const friendOverlaps = overlaps.filter((o) =>
    o.friends.some((f) => f.user.id === friend.id)
  );

  // For free users, only show overlaps within the 14-day horizon
  const horizon = new Date();
  horizon.setDate(horizon.getDate() + FREE_OVERLAP_HORIZON_DAYS);
  const horizonStr = toDateStr(horizon);
  const visibleOverlaps = isPremium
    ? friendOverlaps
    : friendOverlaps.filter((o) => o.startDate <= horizonStr);
  const hiddenCount = friendOverlaps.length - visibleOverlaps.length;

  const totalDays = visibleOverlaps.reduce((sum, o) => sum + o.days, 0);

  return (
    <div
      className="bg-slate-800 rounded-xl p-3 border border-slate-700/50 active:bg-slate-750 transition-colors cursor-pointer"
      onClick={() => { if (friendOverlaps.length > 0) { haptic(); setExpanded((prev) => !prev); } }}
    >
      <div className="flex items-center gap-3">
        <button
          onClick={(e) => { e.stopPropagation(); haptic(); setShowProfile(true); }}
          className="flex items-center gap-3 flex-1 min-w-0 active:opacity-70 transition-opacity text-left"
        >
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold shrink-0 overflow-hidden"
            style={{ backgroundColor: airline.color, color: airline.textOnColor }}
          >
            <AvatarImg src={friend.avatarUrl} name={friend.name} />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-slate-100">{friend.name}</span>
              <span
                className="text-xs px-1.5 py-0.5 rounded-full font-semibold"
                style={{ backgroundColor: airline.color, color: airline.textOnColor }}
              >
                {airline.name}
              </span>
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              @{friend.handle} · Base: <span className="font-mono">{friend.baseAirport}</span> ({getCityName(friend.baseAirport)})
            </div>
          </div>
        </button>
        <div className="flex items-center gap-1.5 shrink-0">
          {friendOverlaps.length > 0 && (
            <span className="flex items-center gap-1 text-xs font-bold text-coral bg-coral/10 px-2 py-0.5 rounded-full">
              {totalDays}d
              {expanded ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
            </span>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              haptic();
              const firstOverlap = friendOverlaps[0];
              const prefill = firstOverlap
                ? `Hey! Saw we overlap in ${getCityName(firstOverlap.city)}. Want to meet up?`
                : undefined;
              setChatTarget({ partnerId: friend.id, prefill });
            }}
            className="p-2.5 -m-1 rounded-lg bg-accent/10 text-accent transition-colors"
            title="Send message"
          >
            <MessageCircle size={16} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              haptic();
              toggleFriendMute(friend.id);
            }}
            className={`p-2.5 -m-1 rounded-lg transition-colors ${
              isMuted
                ? 'bg-slate-700 text-slate-500'
                : 'bg-accent/10 text-accent'
            }`}
            title={isMuted ? 'Unmute notifications' : 'Mute notifications'}
          >
            {isMuted ? <BellOff size={16} /> : <Bell size={16} />}
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setConfirmRemove(true);
            }}
            className="p-2.5 -m-1 rounded-lg bg-slate-700/50 text-slate-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
            title="Remove friend"
          >
            <UserMinus size={16} />
          </button>
        </div>
      </div>

      {/* Remove confirmation */}
      {confirmRemove && (
        <div className="mt-2 pt-2 border-t border-slate-700/50 flex items-center justify-between">
          <span className="text-xs text-slate-400">Remove {friend.name}?</span>
          <div className="flex gap-2">
            <button
              onClick={() => setConfirmRemove(false)}
              className="px-3 py-1 text-xs text-slate-400 hover:text-slate-200 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => { hapticMedium(); removeFriend(friend.id).catch((err) => console.warn('[FriendCard] remove failed:', err)); }}
              className="px-3 py-1 text-xs text-red-400 bg-red-500/10 rounded-lg hover:bg-red-500/20 transition-colors"
            >
              Remove
            </button>
          </div>
        </div>
      )}

      {expanded && friendOverlaps.length > 0 && (
        <div className="mt-2 pt-2 border-t border-slate-700/50 flex flex-col gap-1 animate-fade-in">
          {visibleOverlaps.map((o) => (
            <div key={o.id} className="flex items-center gap-1.5 text-xs">
              <MapPin size={11} className="text-coral shrink-0" />
              <span className="text-slate-300 font-medium">{getCityName(o.city)}</span>
              <span className="text-slate-500 font-mono text-xs">{o.city}</span>
              <span className="text-slate-500 ml-auto text-xs">
                {formatDateShort(o.startDate)}
                {o.startDate !== o.endDate && `–${formatDateShort(o.endDate)}`}
              </span>
              <span className="text-coral text-xs font-semibold">{o.days}d</span>
            </div>
          ))}
          {hiddenCount > 0 && (
            <button
              onClick={() => setShowUpsell(true)}
              className="flex items-center gap-1.5 text-xs text-amber-400 font-semibold mt-0.5 active:opacity-70"
            >
              <Crown size={11} />
              +{hiddenCount} more overlap{hiddenCount !== 1 ? 's' : ''} — unlock with Premium
            </button>
          )}
        </div>
      )}

      {showProfile && (
        <FriendProfileSheet
          friendId={friend.id}
          onClose={() => setShowProfile(false)}
        />
      )}

      {chatTarget && (
        <ChatView
          partnerId={chatTarget.partnerId}
          prefill={chatTarget.prefill}
          onClose={() => setChatTarget(null)}
        />
      )}

      <PremiumUpsellSheet open={showUpsell} onClose={() => setShowUpsell(false)} />
    </div>
  );
}
